import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FormlyService, Script } from '@nx-ahs/formly/shared';
import { FormlyCollectorModalComponent } from '@nx-ahs/formly/runner';

@Injectable({
	providedIn: 'root'
})
export class FormlyBuilderApiService {
	constructor(
		private httpClient: HttpClient,
		private formlyService: FormlyService
	) {}

	private get baseApiUrl(): string {
		return this.formlyService.baseApiUrl;
	}

	/**
	 * Add a tag to be associated with the script
	 *
	 * @param tagInput - Tag the user has input.
	 * @param scriptId - ScriptId the tag is associated with.
	 * @returns A Promise resolving to a success message or throwing an error on failure.
	 */
	public async addTagToScript(tagInput: string, scriptId: number): Promise<string> {
		try {
			const apiUrl = `${this.baseApiUrl}api/contract/AddTagToScript?scriptId=${scriptId}&tagInput=${tagInput}`;

			// returns true/false
			const result: any = await firstValueFrom(this.httpClient.post(apiUrl, { observe: 'response' }));

			if (result) {
				return `Tag "${tagInput}" added successfully.`;
			} else {
				throw new Error(result.body?.message || 'Unknown error occurred.');
			}
		} catch (error: any) {
			console.error(`Error add tag to script (${scriptId}):`, error.message || error);
			throw new Error(error?.message || 'Failed to add the tag. Please try again.');
		}
	}

	/**
	 * Get all scripts from Script.Script (including deleted). Maybe add a flag to include deleted or not eventually
	 */
	public async getAllScripts(): Promise<any[]> {
		const url = `${this.baseApiUrl}api/contract/GetAllScripts`;
		return await firstValueFrom(this.httpClient.get<any[]>(url));
	}

	/**
	 * Manage a script by adding or deleting it.
	 *
	 * @param action - The action to perform: 'add' to create a new script or 'delete' to remove an existing script.
	 * @param scriptData - Data for the script. For 'add', `scriptId`, `name`, and `description` are required.
	 *                      For 'delete', only `scriptId` is required.
	 * @returns A Promise Script data on success or throwing an error on failure.
	 */
	public async manageScript(action: 'add' | 'delete', scriptData: Script): Promise<Script> {
		try {
			let apiUrl = '';

			// Determine the endpoint based on the action
			if (action === 'add') {
				apiUrl = `${this.baseApiUrl}api/contract/CreateNewScript`;
			} else if (action === 'delete') {
				apiUrl = `${this.baseApiUrl}api/contract/DeleteScript`;
			} else {
				throw new Error('Invalid action. Use "add" or "delete".');
			}

			// Call the API and handle the response
			const result: any = await firstValueFrom(this.httpClient.post(apiUrl, scriptData, { observe: 'response' }));

			if (result.status === 200) {
				return result.body as Script;
			} else {
				throw new Error(result.body?.message || 'Unknown error occurred.');
			}
		} catch (error: any) {
			console.error(`Error managing script (${action}):`, error.message || error);
			throw new Error(error?.message || 'Failed to manage the script. Please try again.');
		}
	}

	/**
	 * Opens a Formly modal dialog with the given script information.
	 *
	 * @param scriptInfo - The information needed to initialize the dialog.
	 * @param scriptInfo.scriptId - The unique ID of the script.
	 * @param scriptInfo.title - The title of the modal.
	 * @param scriptInfo.overrides - Optional overrides for the script behavior.
	 * @param scriptInfo.linkedEntityNameId - Optional ID of the linked entity name.
	 * @param scriptInfo.linkedEntityId - Optional ID of the linked entity.
	 * @param scriptInfo.clientOnly - Whether the modal is client-side only.
	 * @param matDialog - The MatDialog instance used to open the modal.
	 * @returns A promise that resolves with the result after the modal is closed.
	 */
	public openFormlyDialog(
		scriptInfo: {
			scriptId: number;
			title: string;
			overrides?: object;
			linkedEntityNameId?: number;
			linkedEntityId?: number;
			clientOnly?: boolean;
		},
		matDialog: MatDialog
	): Promise<any> {
		return matDialog
			.open(FormlyCollectorModalComponent, {
				data: scriptInfo
			})
			.afterClosed()
			.toPromise();
	}

	public async saveScriptInfo(params: {
		scriptId: number;
		script: { scriptId: number; name: string; description: string; notes?: string };
	}): Promise<object> {
		const result = await firstValueFrom(
			this.httpClient.post(`${this.baseApiUrl}api/contract/SaveScriptInfo`, params)
		);

		return result;
	}

	public async saveScriptFields(params: { scriptData: string; scriptId: number }): Promise<object> {
		const result = await firstValueFrom(
			this.httpClient.post(`${this.baseApiUrl}api/contract/SaveScriptFields`, params)
		);

		return result;
	}
}
